// What the Transfer-to-Bank flow (components/CashoutFlow) needs, read from one
// GET /orders/earnings answer. Shared by the Requests page and the Balance
// page, which since 1.5.2 b49 opens the same flow over itself instead of
// jumping to Requests (founder: "it should keep you on balance page").
import { minCashoutInfoFrom } from "./minCashoutLock";
import { normCur, num, type EarningsTotals } from "./requestsFormat";
import type { MasspayHold, MinCashoutInfo, RefundFeesOwed } from "../types/orders";

export type CashoutData = {
  earningsByCur: EarningsTotals;
  commissionRate: number | null;
  refundFeesByCur: Record<string, RefundFeesOwed>;
  minCashoutInfo: MinCashoutInfo;
  masspayHold: MasspayHold | null;
};

export function cashoutDataFrom(earnings: any, fallbackCurrency?: string | null): CashoutData {
  // Normalise the earnings: both { byCurrency } and the old { total }.
  const payload = earnings || {};
  const byCur: EarningsTotals = {};

  if (payload.byCurrency && typeof payload.byCurrency === "object" && !Array.isArray(payload.byCurrency)) {
    for (const [k, v] of Object.entries<any>(payload.byCurrency)) {
      const cur = normCur(k);
      const prev = byCur[cur] || { total: 0, count: 0 };
      byCur[cur] = {
        total: prev.total! + num(v?.total),
        count: prev.count! + num(v?.count),
      };
    }
  } else if (typeof payload.total === "number") {
    byCur[normCur(fallbackCurrency || "usd")] = { total: num(payload.total) };
  }

  // Refer & Earn money added to the balance: cashes out with the requests, no fee on it.
  for (const [k, v] of Object.entries(payload.referralByCurrency || {})) {
    const cur = normCur(k);
    if (num(v) > 0) byCur[cur] = { ...(byCur[cur] || { total: 0, count: 0 }), referral: num(v) };
  }

  return {
    earningsByCur: byCur,
    // Talent's commission rate, resolved server-side (0.15 default or admin
    // override). Absent on an older server response → null → modal shows 15%.
    commissionRate: typeof payload.commissionRate === "number" ? payload.commissionRate : null,
    // Owed refund card fees, per currency ({ amount, count, items }). Only
    // ever present after this talent refunded a played request.
    refundFeesByCur:
      payload.refundFees && typeof payload.refundFees === "object" && !Array.isArray(payload.refundFees)
        ? payload.refundFees
        : {},
    // Minimum-cashout facts (absent on an older server → mirror + no waiver).
    minCashoutInfo: minCashoutInfoFrom(payload),
    masspayHold: payload.masspayHold ?? null,
  };
}
